import React, { useState, useEffect } from 'react'
import { Phone, Search, ShoppingCart, Loader2 } from 'lucide-react'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from '../../../../store'
import { fetchAvailableNumbers, acquirePhoneNumber, clearAvailableNumbers, fetchPhoneNumbers } from '../../../../lib/redux/phone-numbers/slice'
import { Typography } from '../../../../interfaces/typography'
import { useUserData } from '../../../../lib/redux/users/user-data-hook'

interface PhoneNumberSelectorProps {
  value: string
  onChange: (value: string) => void
}

export const PhoneNumberSelector: React.FC<PhoneNumberSelectorProps> = ({
  value,
  onChange
}) => {
  const dispatch = useDispatch<AppDispatch>()
  const {
    phoneNumbers,
    availableNumbers,
    isLoading: numbersLoading,
    isAcquiring
  } = useSelector((state: RootState) => state.phoneNumbers)
  const { routes } = useSelector((state: RootState) => state.inboundRoutes)
  const { allUsers } = useUserData()
  
  const [mode, setMode] = useState<'existing' | 'purchase'>('existing')
  const [showPurchaseSearch, setShowPurchaseSearch] = useState(false)
  const [searchParams, setSearchParams] = useState({
    areaCode: '',
    country: 'US'
  })

  // Get phone numbers already used in routes
  const usedPhoneNumbers = new Set(
    routes
      .filter(route => route.address_type === 'PHONE')
      .map(route => route.address)
  )

  // Normalize phone number format (remove + prefix for comparison)
  const normalizePhone = (phone: string) => phone.replace(/^\+/, '')

  // Get user name from user_id
  const getUserName = (userId: string | null): string | null => {
    if (!userId) return null
    const user = allUsers.find(u => u.user_id === userId)
    return user ? user.user_name : null
  }

  // Get available phone numbers:
  // 1. Team-owned numbers (user_id is null)
  // 2. Not already used in routes (unless it's the current value when editing)
  const availableTeamPhoneNumbers = phoneNumbers.filter(
    phone => {
      // Only show numbers with no user_id (team-owned, not user-assigned)
      if (phone.user_id !== null) {
        return false;
      }

      const normalizedPhone = normalizePhone(phone.number)
      const normalizedValue = normalizePhone(value)

      // Check if already used in a route
      const isUsed = usedPhoneNumbers.has(normalizedPhone);
      const isCurrentValue = normalizedPhone === normalizedValue;

      if (isUsed && !isCurrentValue) {
        return false;
      }

      return true;
    }
  )

  // Get rep-assigned numbers:
  // 1. Numbers with user_id (assigned to reps)
  // 2. Not already used in routes (unless it's the current value when editing)
  const availableRepPhoneNumbers = phoneNumbers.filter(
    phone => {
      // Only show numbers with a user_id (rep-assigned)
      if (phone.user_id === null) {
        return false;
      }

      const normalizedPhone = normalizePhone(phone.number)
      const normalizedValue = normalizePhone(value)

      // Check if already used in a route
      const isUsed = usedPhoneNumbers.has(normalizedPhone);
      const isCurrentValue = normalizedPhone === normalizedValue;

      if (isUsed && !isCurrentValue) {
        return false;
      }

      return true;
    }
  )

  // Combined count for display
  const totalAvailableNumbers = availableTeamPhoneNumbers.length + availableRepPhoneNumbers.length

  useEffect(() => {
    // Only auto-set mode on initial load when there's a value (editing existing route)
    if (value && mode === 'existing') {
      // Stay in existing mode - this is for editing
      return
    }

    // Auto-switch to purchase mode if no available numbers and we're in existing mode
    if (totalAvailableNumbers === 0 && mode === 'existing') {
      setMode('purchase')
    }
  }, [totalAvailableNumbers, value])

  // Separate effect for initial mode setting when editing
  useEffect(() => {
    // Only run this once when component mounts with a value
    if (value) {
      setMode('existing')
    }
  }, []) // Empty dependency array - only runs once on mount

  const handleSearchPhoneNumbers = async () => {
    if (!searchParams.areaCode && searchParams.country === 'US') {
      alert('Please enter an area code for US numbers')
      return
    }
    
    try {
      await dispatch(fetchAvailableNumbers({
        area_code: searchParams.areaCode,
        iso_country_code: searchParams.country,
        number_type: 'Local'
      })).unwrap()
      setShowPurchaseSearch(true)
    } catch (error) {
      console.error('Failed to search phone numbers:', error)
      alert('Failed to search for available phone numbers. Please try again.')
    }
  }

  const handlePurchasePhoneNumber = async (number: any) => {
    const success = await dispatch(acquirePhoneNumber(number))
      .unwrap()
      .then(() => true)
      .catch(error => {
        console.error('Failed to purchase phone number:', error)
        if (error.kind === 'API') {
          alert('Failed to purchase phone number: ' + error.message)
        } else {
          alert('Unexpected error purchasing phone number. Please try again.')
        }
        return false
      })
    if (!success) { return }
    onChange(normalizePhone(number.phone_number))
    setShowPurchaseSearch(false)
    dispatch(clearAvailableNumbers())
    // Refresh owned numbers
    dispatch(fetchPhoneNumbers())
  }

  return (
    <div className="space-y-2">
      {/* Mode Selection */}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setMode('existing')}
          disabled={totalAvailableNumbers === 0}
          className={`px-3 py-2 rounded-md transition-colors ${
            mode === 'existing'
              ? 'bg-blue-100 border border-blue-300'
              : totalAvailableNumbers === 0
                ? 'bg-gray-50 border border-gray-200 cursor-not-allowed'
                : 'bg-white border border-gray-300 hover:bg-gray-50'
          }`}
        >
          <Typography
            variant="smallParagraph"
            color={mode === 'existing' ? '#2563EB' : totalAvailableNumbers === 0 ? '#9CA3AF' : '#374151'}
            fontWeight={500}
          >
            Owned ({totalAvailableNumbers})
          </Typography>
        </button>
        <button
          type="button"
          onClick={() => setMode('purchase')}
          className={`px-3 py-2 rounded-md transition-colors ${
            mode === 'purchase'
              ? 'bg-green-100 border border-green-300'
              : 'bg-white border border-gray-300 hover:bg-gray-50'
          }`}
        >
          <Typography
            variant="smallParagraph"
            color={mode === 'purchase' ? '#059669' : '#374151'}
            fontWeight={500}
          >
            Buy New
          </Typography>
        </button>
      </div>

      {/* Existing Numbers Mode */}
      {mode === 'existing' && (
        <div>
          {phoneNumbers.length === 0 ? (
            <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
              <Typography variant="smallParagraph" color="#6B7280">
                Loading phone numbers...
              </Typography>
            </div>
          ) : totalAvailableNumbers === 0 ? (
            <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
              <Typography variant="smallParagraph" color="#6B7280">
                No available numbers. Try "Buy New" to purchase a phone number.
              </Typography>
            </div>
          ) : (
            <select
              value={value}
              onChange={(e) => onChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              style={{
                fontSize: '14px',
                fontFamily: 'Calibri,Arial,Helvetica,sans-serif',
                color: value ? '#1F2937' : '#6B7280'
              }}
            >
              <option value="">Choose owned number...</option>

              {/* Team-owned numbers section */}
              {availableTeamPhoneNumbers.length > 0 && (
                <optgroup label="Team Numbers">
                  {availableTeamPhoneNumbers.map((phone) => (
                    <option key={phone.number} value={normalizePhone(phone.number)}>
                      {phone.number} - {phone.locality}, {phone.region}
                    </option>
                  ))}
                </optgroup>
              )}

              {/* Rep-assigned numbers section */}
              {availableRepPhoneNumbers.length > 0 && (
                <optgroup label="Rep-Assigned Numbers">
                  {availableRepPhoneNumbers.map((phone) => {
                    const repName = getUserName(phone.user_id)
                    return (
                      <option key={phone.number} value={normalizePhone(phone.number)}>
                        {phone.number} - {phone.locality}, {phone.region} ({repName || 'Unknown Rep'})
                      </option>
                    )
                  })}
                </optgroup>
              )}
            </select>
          )}
        </div>
      )}

      {/* Purchase New Mode */}
      {mode === 'purchase' && (
        <div className="space-y-3">
          <div className="grid grid-cols-4 gap-2">
            <select
              value={searchParams.country}
              onChange={(e) => setSearchParams(prev => ({ ...prev, country: e.target.value }))}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
              style={{
                fontSize: '14px',
                fontFamily: 'Calibri,Arial,Helvetica,sans-serif',
                color: '#1F2937'
              }}
            >
              <option value="US">🇺🇸 US</option>
              <option value="CA">🇨🇦 CA</option>
              <option value="GB">🇬🇧 GB</option>
              <option value="MX">🇲🇽 MX</option>
            </select>
            <input
              type="text"
              value={searchParams.areaCode}
              onChange={(e) => setSearchParams(prev => ({ ...prev, areaCode: e.target.value }))}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
              placeholder="Area code"
              maxLength={3}
              style={{
                fontSize: '14px',
                fontFamily: 'Calibri,Arial,Helvetica,sans-serif',
                color: '#1F2937'
              }}
            />
            <button
              type="button"
              onClick={handleSearchPhoneNumbers}
              disabled={numbersLoading}
              className="col-span-2 px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center justify-center"
            >
              <Search className="w-4 h-4 mr-2" />
              <Typography variant="smallParagraph" color="#FFFFFF" fontWeight={500}>
                {numbersLoading ? 'Searching...' : 'Search Numbers'}
              </Typography>
            </button>
          </div>

          {showPurchaseSearch && availableNumbers.length > 0 && (
            <div className="border border-green-200 rounded-lg p-4 bg-green-50">
              <Typography variant="smallParagraph" color="#059669" fontWeight={500} style={{ marginBottom: '0.75rem' }}>
                Found {availableNumbers.length} available numbers:
              </Typography>
              <div className="max-h-48 overflow-y-auto space-y-2">
                {availableNumbers.slice(0, 5).map((number, index) => (
                  <div key={index} className="flex items-center justify-between bg-white p-3 rounded-lg border border-gray-200">
                    <div className="flex-1">
                      <Typography variant="smallParagraph" color="#1F2937" fontWeight={500}>
                        {number.phone_number}
                      </Typography>
                      <Typography variant="smallParagraph" color="#6B7280" style={{ marginTop: '0.125rem' }}>
                        {number.locality}, {number.region}
                      </Typography>
                    </div>
                    <button
                      type="button"
                      onClick={() => handlePurchasePhoneNumber(number)}
                      disabled={isAcquiring}
                      className="px-3 py-1.5 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                    >
                      {isAcquiring ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <ShoppingCart className="w-4 h-4" />
                      )}
                      <Typography variant="smallParagraph" color="#FFFFFF" fontWeight={500}>
                        {isAcquiring ? 'Purchasing...' : 'Purchase'}
                      </Typography>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {showPurchaseSearch && availableNumbers.length === 0 && !numbersLoading && (
            <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
              <Typography variant="smallParagraph" color="#6B7280">
                No numbers found for the specified criteria. Try a different area code.
              </Typography>
            </div>
          )}
        </div>
      )}
    </div>
  )
}